import { Box, Grid, Skeleton, styled } from '@mui/material';
import React, { useState } from 'react';
import SplitPane, { Pane } from 'split-pane-react';
import 'split-pane-react/esm/themes/default.css';
import { WsColors, Item } from 'wisestep-base';

import HandleSashRender from './HandleSashRender';

interface ResizableProps {
  leftPanel: React.ReactElement;
  rightPanel: React.ReactElement;
  minSize?: string;
  maxSize?: string;
  defaultSizes?: (string | number)[];
  isLoading?: boolean;
  isDocument?: boolean;
  leftVerticalScroll?: boolean;
  rightVerticalScroll?: boolean;
  pseudoDragValue?: boolean;
  setPseudoDragValue?: (boolean) => void;
}

const Resizable: React.FC<ResizableProps> = ({
  leftPanel,
  rightPanel,
  minSize = '20%',
  maxSize = '80%',
  leftVerticalScroll = true,
  rightVerticalScroll = true,
  pseudoDragValue = true,
  setPseudoDragValue = () => {},
  defaultSizes = ['50%', 'auto'],
  isLoading = false,
  isDocument,
}) => {
  const [sizes, setSizes] = useState<(string | number)[]>(defaultSizes);
  const showSkeleton = isLoading;
  const SkeletonUI = styled(Skeleton)(() => ({
    backgroundColor: WsColors.TertiaryShades100,
  }));

  const updateLayoutSizes = (sizes: (string | number)[]) => {
    setSizes(sizes);
    // To assign event
    const event = new CustomEvent('layout-resize', {
      detail: sizes,
    });
    // To trigger the event Listener
    document.dispatchEvent(event);
  };

  const leftPaneDesign = {
    '&::-webkit-scrollbar': {
      width: '10px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#888',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
    overflowX: 'hidden',
    overflowY: leftVerticalScroll ? 'scroll' : 'hidden',
    height: '100%',
  };

  const rightPaneDesign = {
    ...leftPaneDesign,
    overflowY: rightVerticalScroll ? 'scroll' : 'hidden',
  };

  return (
    <Box height="100%" id="resizablePane">
      {showSkeleton ? (
        <Box display="flex" style={{ backgroundColor: WsColors.White }}>
          <Box style={{ width: minSize, height: '100%', marginLeft: '10px', marginRight: '10px' }}>
            <Grid item sx={{ height: '100%', width: '100%' }}>
              <Grid item spacing={1} direction="row" sx={{ mt: 1 }}>
                <Grid container spacing={1} direction="row" sx={{ mt: 1 }}>
                  <Grid item xs={6} md={6}>
                    <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item spacing={1} direction="row" sx={{ mt: 1 }}>
                <SkeletonUI animation="wave" variant="rectangular" width="100%" height={2} />
              </Grid>
              <Grid item spacing={1} direction="row" sx={{ mt: 2 }}>
                <SkeletonUI animation="wave" variant="rectangular" width="100%" height={200} />
              </Grid>
              <Grid item spacing={1} direction="row" sx={{ mt: 1 }}>
                <SkeletonUI animation="wave" variant="rectangular" width="100%" height={200} />
              </Grid>
              <Grid item spacing={1} direction="row" sx={{ mt: 1 }}>
                <SkeletonUI animation="wave" variant="rectangular" width="100%" height={200} />
              </Grid>
            </Grid>

            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
            ></Box>
          </Box>
          <Box style={{ width: 'auto', flexGrow: 1 }}>
            {isDocument ? (
              <Grid
                item
                sx={{
                  height: '100%',
                  width: 'auto',
                  borderLeft: `3px solid ${WsColors.BorderGrey}`,
                }}
              >
                <Item>
                  <Grid container direction="column" spacing={1}>
                    <Grid item sx={{ mt: 1 }}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={2} />
                    </Grid>
                    <Grid item sx={{ mt: 1 }}>
                      <SkeletonUI
                        animation="wave"
                        variant="rectangular"
                        width="100%"
                        height={800}
                      />
                    </Grid>
                  </Grid>
                </Item>
              </Grid>
            ) : (
              <Grid
                item
                sx={{
                  height: '100%',
                  width: 'auto',
                  borderLeft: `3px solid ${WsColors.BorderGrey}`,
                }}
              >
                <Item>
                  <Grid spacing={1} direction="row">
                    <SkeletonUI animation="wave" variant="rectangular" width="100%" height={100} />
                  </Grid>
                  <Grid container spacing={1} direction="row" sx={{ mt: 1 }}>
                    <Grid item xs={2} md={2}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                    </Grid>
                    <Grid item xs={2} md={2}>
                      <SkeletonUI animation="wave" variant="rectangular" width="100%" height={40} />
                    </Grid>
                  </Grid>

                  <Grid spacing={1} direction="row" sx={{ mt: 1 }}>
                    <SkeletonUI animation="wave" variant="rectangular" width="100%" height={2} />
                  </Grid>
                  <Grid spacing={1} direction="row" sx={{ mt: 2 }}>
                    <SkeletonUI animation="wave" variant="rectangular" width="40%" height={40} />
                  </Grid>
                  <Grid container spacing={1} direction="row" sx={{ ml: 5, mt: 1 }}>
                    <Grid item xs={6} md={6}>
                      <SkeletonUI animation="wave" variant="rectangular" width="80%" height={40} />
                    </Grid>
                    <Grid item xs={6} md={6} sx={{ ml: -10 }}>
                      <SkeletonUI animation="wave" variant="rectangular" width="80%" height={40} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={1} direction="row" sx={{ mt: 2 }}>
                    <Grid item xs={6} md={6}>
                      <SkeletonUI animation="wave" variant="circular" width="8%" height={45} />
                    </Grid>
                    <Grid item xs={6} md={6} sx={{ ml: -60 }}>
                      <SkeletonUI animation="wave" variant="rectangular" width="75%" height={40} />
                    </Grid>
                  </Grid>

                  <Grid
                    sx={{
                      borderLeft: `3px solid ${WsColors.BorderGrey}`,
                      ml: '22px',
                    }}
                  >
                    <Grid spacing={1} direction="row" sx={{ ml: 5, mt: 2 }}>
                      <SkeletonUI
                        animation="wave"
                        variant="rectangular"
                        width="100%"
                        height={100}
                      />
                    </Grid>
                    <Grid container spacing={1} direction="row" sx={{ ml: 5, mt: 1 }}>
                      <Grid item xs={6} md={6}>
                        <SkeletonUI
                          animation="wave"
                          variant="rectangular"
                          width="80%"
                          height={40}
                        />
                      </Grid>
                      <Grid item xs={6} md={6} sx={{ ml: -10 }}>
                        <SkeletonUI
                          animation="wave"
                          variant="rectangular"
                          width="80%"
                          height={40}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid container spacing={1} direction="row" sx={{ mt: 2 }}>
                    <Grid item xs={6} md={6}>
                      <SkeletonUI animation="wave" variant="circular" width="8%" height={45} />
                    </Grid>
                    <Grid item xs={6} md={6} sx={{ ml: -60 }}>
                      <SkeletonUI animation="wave" variant="rectangular" width="75%" height={40} />
                    </Grid>
                  </Grid>
                  <Grid spacing={1} direction="row" sx={{ ml: 8, mt: 1 }}>
                    <SkeletonUI animation="wave" variant="rectangular" width="100%" height={100} />
                  </Grid>
                </Item>
              </Grid>
            )}
            {/* <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
            ></Box> */}
          </Box>
        </Box>
      ) : (
        <SplitPane
          split="vertical"
          sizes={sizes}
          onChange={updateLayoutSizes}
          onDragEnd={() => setPseudoDragValue(!pseudoDragValue)}
          sashRender={HandleSashRender}
          performanceMode={false}
          style={{ height: 'calc(100vh - 64px)' }}
        >
          <Pane minSize={minSize} maxSize={maxSize} style={{ height: '100%' }}>
            <Box display="flex" flexDirection="column" sx={leftPaneDesign}>
              {leftPanel}
            </Box>
          </Pane>
          <Pane style={{ height: '100%' }}>
            <Box display="flex" flexDirection="column" sx={rightPaneDesign}>
              {rightPanel}
            </Box>
          </Pane>
        </SplitPane>
      )}
    </Box>
  );
};

export default Resizable;
